// ========================================
// pages/BeneficiaryClaim.js - Claim Inherited Assets
// ========================================

import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';

const BeneficiaryClaim = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [accepting, setAccepting] = useState(false);
  const [showAssets, setShowAssets] = useState(false);
  const [claimData, setClaimData] = useState(null);
  const [assets, setAssets] = useState([]);
  const [deceasedName, setDeceasedName] = useState('');
  const [nokName, setNokName] = useState('');
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [showAssetDetails, setShowAssetDetails] = useState(false);

  useEffect(() => {
    fetchClaimData();
  }, [token]);

  const fetchClaimData = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/assets/claims/${token}`);
      setClaimData(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid or expired claim link');
    } finally {
      setLoading(false);
    }
  };

  const handleAccept = async () => {
    setAccepting(true);
    try {
      const response = await axios.post(`${process.env.REACT_APP_API_URL}/api/assets/claims/${token}/accept`);
      setAssets(response.data.assets);
      setDeceasedName(response.data.deceasedName);
      setNokName(response.data.nokName);
      setShowAssets(true);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to accept claim');
    } finally {
      setAccepting(false);
    }
  };

  const viewAssetDetails = (asset) => {
    setSelectedAsset(asset);
    setShowAssetDetails(true);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-GH', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.spinner}></div>
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.errorIcon}>🔒</div>
          <h2 style={styles.errorTitle}>Invalid Claim Link</h2>
          <p style={styles.errorText}>{error}</p>
          <p style={styles.errorNote}>
            This link may have expired or already been used.
            Please contact the estate administrator if you believe this is an error.
          </p>
          <Link to="/" style={styles.homeButton}>
            Go to Homepage
          </Link>
        </div>
      </div>
    );
  }

  if (showAssets) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.headerIcon}>📜</div>
          <h1 style={styles.title}>Your Inheritance</h1>
          
          <div style={styles.infoBox}>
            <p style={styles.infoText}>
              <strong>{nokName}</strong>, you have been named as a beneficiary in the estate of 
              <strong> {deceasedName}</strong>.
            </p>
            <p style={styles.infoSubtext}>
              Below are the assets left to you.
            </p>
          </div>

          {assets.length === 0 ? (
            <div style={styles.emptyState}>
              <p>No assets found.</p>
            </div>
          ) : (
            <div style={styles.assetsList}>
              <h3 style={styles.assetsTitle}>Your Inherited Assets ({assets.length})</h3>
              
              {assets.map((asset) => (
                <div key={asset.id} style={styles.assetCard}>
                  <div style={styles.assetHeader}>
                    <div style={styles.assetIcon}>{asset.icon || '📦'}</div>
                    <div style={styles.assetInfo}>
                      <h4 style={styles.assetName}>{asset.assetName}</h4>
                      <p style={styles.assetType}>{asset.assetType?.replace('_', ' ')}</p>
                    </div>
                    <button
                      onClick={() => viewAssetDetails(asset)}
                      style={styles.detailsButton}
                    >
                      View Details
                    </button>
                  </div>
                  
                  <div style={styles.assetFooter}>
                    <span style={styles.assetDate}>
                      Added: {formatDate(asset.createdAt)}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Asset Details Modal */}
        {showAssetDetails && selectedAsset && (
          <div style={styles.modalOverlay} onClick={() => setShowAssetDetails(false)}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              <button style={styles.modalClose} onClick={() => setShowAssetDetails(false)}>×</button>
              <h3 style={styles.modalTitle}>Asset Details</h3>
              
              <div style={styles.modalDetails}>
                <p><strong>Asset:</strong> {selectedAsset.assetName}</p>
                <p><strong>Type:</strong> {selectedAsset.assetType?.replace('_', ' ')}</p>
                {selectedAsset.institutionName && (
                  <p><strong>Institution:</strong> {selectedAsset.institutionName}</p>
                )}
                {selectedAsset.accountNumber && (
                  <p><strong>Account Number:</strong> <span style={styles.revealedText}>{selectedAsset.accountNumber}</span></p>
                )}
                {selectedAsset.balance && (
                  <p><strong>Balance:</strong> GHS {selectedAsset.balance.toLocaleString()}</p>
                )}
                {selectedAsset.estimatedValue && (
                  <p><strong>Value:</strong> GHS {selectedAsset.estimatedValue.toLocaleString()}</p>
                )}
                {selectedAsset.address && (
                  <p><strong>Address:</strong> {selectedAsset.address}</p>
                )}
                {selectedAsset.titleDeedNumber && (
                  <p><strong>Title Deed:</strong> {selectedAsset.titleDeedNumber}</p>
                )}
                {selectedAsset.make && (
                  <p><strong>Vehicle:</strong> {selectedAsset.make} {selectedAsset.model} ({selectedAsset.year})</p>
                )}
                {selectedAsset.vin && (
                  <p><strong>VIN:</strong> {selectedAsset.vin}</p>
                )}
                {selectedAsset.description && (
                  <p><strong>Description:</strong> {selectedAsset.description}</p>
                )}
              </div>
              
              <div style={styles.modalFooter}>
                <button
                  onClick={() => setShowAssetDetails(false)}
                  style={styles.modalDoneButton}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }

  // Acceptance Page
  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.headerIcon}>📜</div>
        <h1 style={styles.title}>Inheritance Claim</h1>
        
        <div style={styles.acceptBox}>
          <p style={styles.acceptText}>
            <strong>{claimData?.nokName}</strong>, you have been named as a beneficiary in the estate of 
            <strong> {claimData?.deceasedName}</strong>.
          </p>
          <p style={styles.acceptSubtext}>
            By accepting, you will be able to view and claim your inherited assets.
          </p>
        </div>

        <div style={styles.buttonGroup}>
          <button
            onClick={handleAccept}
            disabled={accepting}
            style={styles.acceptButton}
          >
            {accepting ? 'Processing...' : '✓ Accept Inheritance'}
          </button>
          <Link to="/" style={styles.declineButton}>
            Decline
          </Link>
        </div>

        <p style={styles.note}>
          By accepting, you confirm that you are the rightful beneficiary.
        </p>
      </div>
    </div>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F0E6',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    padding: '20px',
  },
  card: {
    backgroundColor: 'white',
    borderRadius: '10px',
    padding: '40px',
    maxWidth: '500px',
    width: '100%',
    boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
  },
  headerIcon: {
    fontSize: '48px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  title: {
    fontSize: '28px',
    color: '#FFD700',
    textAlign: 'center',
    marginBottom: '20px',
  },
  acceptBox: {
    backgroundColor: '#F5F0E6',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '30px',
    textAlign: 'center',
  },
  acceptText: {
    fontSize: '16px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  acceptSubtext: {
    fontSize: '14px',
    color: '#666',
  },
  buttonGroup: {
    display: 'flex',
    gap: '15px',
    marginBottom: '20px',
  },
  acceptButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    fontSize: '16px',
    fontWeight: 'bold',
    cursor: 'pointer',
  },
  declineButton: {
    flex: 1,
    padding: '12px',
    backgroundColor: 'transparent',
    color: '#666',
    border: '1px solid #666',
    borderRadius: '5px',
    textDecoration: 'none',
    fontSize: '16px',
    textAlign: 'center',
  },
  note: {
    fontSize: '12px',
    color: '#666',
    textAlign: 'center',
  },
  infoBox: {
    backgroundColor: '#F5F0E6',
    padding: '20px',
    borderRadius: '10px',
    marginBottom: '30px',
    textAlign: 'center',
  },
  infoText: {
    fontSize: '16px',
    color: '#1A1A1A',
    marginBottom: '10px',
  },
  infoSubtext: {
    fontSize: '14px',
    color: '#666',
  },
  assetsList: {
    marginTop: '20px',
  },
  assetsTitle: {
    fontSize: '18px',
    color: '#2E7D32',
    marginBottom: '15px',
  },
  assetCard: {
    backgroundColor: '#F5F0E6',
    borderRadius: '8px',
    padding: '15px',
    marginBottom: '15px',
  },
  assetHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '15px',
    marginBottom: '10px',
  },
  assetIcon: {
    fontSize: '32px',
  },
  assetInfo: {
    flex: 1,
  },
  assetName: {
    fontSize: '16px',
    fontWeight: 'bold',
    color: '#1A1A1A',
    margin: 0,
  },
  assetType: {
    fontSize: '12px',
    color: '#666',
    margin: 0,
    textTransform: 'capitalize',
  },
  detailsButton: {
    padding: '6px 12px',
    backgroundColor: 'transparent',
    border: '1px solid #2E7D32',
    color: '#2E7D32',
    borderRadius: '5px',
    cursor: 'pointer',
    fontSize: '12px',
  },
  assetFooter: {
    display: 'flex',
    justifyContent: 'flex-end',
    borderTop: '1px solid #ddd',
    paddingTop: '10px',
    marginTop: '10px',
  },
  assetDate: {
    fontSize: '11px',
    color: '#999',
  },
  emptyState: {
    textAlign: 'center',
    padding: '40px',
    color: '#666',
  },
  spinner: {
    width: '40px',
    height: '40px',
    border: '4px solid #F5F0E6',
    borderTop: '4px solid #FFD700',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
    margin: '0 auto 20px',
  },
  errorIcon: {
    fontSize: '48px',
    textAlign: 'center',
    marginBottom: '20px',
  },
  errorTitle: {
    fontSize: '24px',
    color: '#dc2626',
    textAlign: 'center',
    marginBottom: '15px',
  },
  errorText: {
    textAlign: 'center',
    color: '#666',
    marginBottom: '10px',
  },
  errorNote: {
    textAlign: 'center',
    fontSize: '12px',
    color: '#999',
    marginBottom: '30px',
  },
  homeButton: {
    display: 'block',
    width: '200px',
    margin: '0 auto',
    padding: '12px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    textAlign: 'center',
    textDecoration: 'none',
    borderRadius: '5px',
    fontWeight: 'bold',
  },
  modalOverlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.7)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalContent: {
    backgroundColor: 'white',
    padding: '30px',
    borderRadius: '10px',
    maxWidth: '500px',
    width: '90%',
    position: 'relative',
  },
  modalClose: {
    position: 'absolute',
    top: '15px',
    right: '15px',
    background: 'none',
    border: 'none',
    fontSize: '24px',
    cursor: 'pointer',
    color: '#666',
  },
  modalTitle: {
    fontSize: '20px',
    color: '#FFD700',
    marginBottom: '20px',
  },
  modalDetails: {
    marginBottom: '20px',
  },
  modalFooter: {
    textAlign: 'center',
  },
  modalDoneButton: {
    padding: '10px 30px',
    backgroundColor: '#FFD700',
    color: '#1A1A1A',
    border: 'none',
    borderRadius: '5px',
    cursor: 'pointer',
    fontWeight: 'bold',
  },
  revealedText: {
    backgroundColor: '#F5F0E6',
    padding: '2px 8px',
    borderRadius: '3px',
    fontFamily: 'monospace',
  },
};

export default BeneficiaryClaim;